import { gql, useApolloClient, useMutation } from '@apollo/client';
import {
  CancelSurfboardPayment,
  CancelSurfboardPaymentVariables,
  CheckSurfboardPaymentStatus,
  CheckSurfboardPaymentStatusVariables,
  CreateSurfboardOrder,
  CreateSurfboardOrderVariables,
  CreateSurfboardPayment,
  CreateSurfboardPaymentVariables,
  RefundSurfboardOrder,
  RefundSurfboardOrderVariables,
} from './__queries__';
import {
  RefundSurfboardInput,
  SurfboardOrderInput,
  SurfboardPaymentInput,
  SurfboardPaymentStatus,
} from '~../__queries__';
import { Logger } from '@datadog/browser-logs';
import { CardDetails } from '~reducers';

const CREATE_ORDER = gql`
  mutation CreateSurfboardOrder($input: SurfboardOrderInput!) {
    initiateSurfboardOrder(input: $input) {
      success
      message
      error
    }
  }
`;

const CREATE_PAYMENT = gql`
  mutation CreateSurfboardPayment($input: SurfboardPaymentInput!) {
    initiateSurfboardPayment(input: $input) {
      success
      message
      error
    }
  }
`;

const CANCEL_PAYMENT = gql`
  mutation CancelSurfboardPayment($paymentId: String!) {
    cancelSurfboardPayment(paymentId: $paymentId) {
      success
      error
    }
  }
`;

const REFUND_PAYMENT = gql`
  mutation RefundSurfboardOrder($input: RefundSurfboardInput!) {
    refundSurfboardOrder(input: $input) {
      success
      error
      message
    }
  }
`;

const CHECK_PAYMENT_STATUS = gql`
  query CheckSurfboardPaymentStatus($id: String!) {
    SurfboardPayment(id: $id) {
      id
      metadata
      status
    }
  }
`;

export class SurfboardPaymentNotFoundError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'SurfboardPaymentNotFoundError';
  }
}

let orderId: string | null = null;
export const useSurfboard = () => {
  const client = useApolloClient();
  const [createOrderMutation] = useMutation<CreateSurfboardOrder, CreateSurfboardOrderVariables>(
    CREATE_ORDER,
  );

  const [createPaymentMutation] = useMutation<
    CreateSurfboardPayment,
    CreateSurfboardPaymentVariables
  >(CREATE_PAYMENT);

  const [cancelPaymentMutation] = useMutation<
    CancelSurfboardPayment,
    CancelSurfboardPaymentVariables
  >(CANCEL_PAYMENT);

  const [refundPaymentMutation] = useMutation<RefundSurfboardOrder, RefundSurfboardOrderVariables>(
    REFUND_PAYMENT,
  );

  async function refundPayment(input: RefundSurfboardInput) {
    const result = await refundPaymentMutation({
      variables: { input },
    });

    return result?.data?.refundSurfboardOrder;
  }

  async function cancelPayment(paymentId: string) {
    return cancelPaymentMutation({
      variables: { paymentId },
    });
  }

  async function createOrder(input: SurfboardOrderInput) {
    const result = await createOrderMutation({
      variables: { input },
    });

    orderId = result.data?.initiateSurfboardOrder?.message || null;

    return result.data?.initiateSurfboardOrder;
  }

  async function createPayment(input: SurfboardPaymentInput) {
    const result = await createPaymentMutation({
      variables: { input },
    });

    return result.data?.initiateSurfboardPayment;
  }

  async function checkStatus(paymentId: string) {
    const result = await client.query<
      CheckSurfboardPaymentStatus,
      CheckSurfboardPaymentStatusVariables
    >({
      query: CHECK_PAYMENT_STATUS,
      variables: {
        id: paymentId,
      },
      fetchPolicy: 'network-only',
    });

    return result.data.SurfboardPayment;
  }

  return {
    orderId,
    createOrder,
    createPayment,
    checkStatus,
    cancelPayment,
    refundPayment,
  };
};

export class SurfboardPaymentInstance {
  id: string;
  intervalId: number | null;
  api: ReturnType<typeof useSurfboard>;
  logger: Logger;
  paymentUpdatesDone: boolean;

  private successHandler: ((details?: CardDetails) => void) | null;
  private failHandler: ((message: string) => void) | null;

  constructor(id: string, api: ReturnType<typeof useSurfboard>, logger: Logger) {
    this.id = id;
    this.api = api;
    this.logger = logger;
    this.intervalId = null;
    this.paymentUpdatesDone = false;
    this.successHandler = null;
    this.failHandler = null;
  }

  onSuccess(handler: (details?: CardDetails) => void) {
    this.successHandler = handler;
  }

  onFail(handler: (message: string) => void) {
    this.failHandler = handler;
  }

  startPolling(interval: number) {
    this.paymentUpdatesDone = false;

    this.intervalId = window.setInterval(this.poll, interval);
  }

  stopPolling() {
    this.paymentUpdatesDone = true;

    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private poll = async () => {
    try {
      const status = await this.api.checkStatus(this.id);

      this.logger.info(`Payment status (${status.status})`, {
        paymentId: this.id,
        status,
      });

      switch (status.status) {
        case SurfboardPaymentStatus.PAYMENT_COMPLETED:
          this.stopPolling();

          if (this.successHandler) {
            const details = status.metadata
              ? {
                  aid: status.metadata.applicationId,
                  cardType: status.metadata.cardBrand,
                  maskedPan: status.metadata.truncatedPan,
                  tvr: status.metadata.terminalVerificationResults,
                }
              : undefined;
            this.successHandler(details);
          }
          break;

        case SurfboardPaymentStatus.PAYMENT_CANCELLED:
          this.stopPolling();

          if (this.failHandler) {
            this.failHandler(SurfboardPaymentStatus.PAYMENT_CANCELLED);
          }

          break;
        case SurfboardPaymentStatus.PAYMENT_FAILED:
          this.stopPolling();

          if (this.failHandler) {
            this.failHandler(SurfboardPaymentStatus.PAYMENT_FAILED);
          }

          break;

        case SurfboardPaymentStatus.PAYMENT_INITIATED:
        case SurfboardPaymentStatus.PAYMENT_PROCESSED:
        case SurfboardPaymentStatus.PAYMENT_PROCESSING:
          // Do nothing, payment still in progress
          break;
      }
    } catch (e) {
      this.logger.error('Payment status threw', {
        err: e,
      });

      // Not used right now
      if (e instanceof SurfboardPaymentNotFoundError) {
        this.paymentUpdatesDone = true;

        this.logger.error('Payment not found - quitting', {
          paymentId: this.id,
        });

        if (this.failHandler) {
          this.failHandler('Technical Error');
        }
      }
    }
  };
}
